import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
    WxbDatePicker,
    WxbIcon,
    WxbInput,
    WxbModal,
    WxbSegmented,
    WxbSelect,
    wxbToast,
} from '../wxb-ui';
import { batchPlanApi } from '../../services/api';
import type { BatchPlanPayload } from '../../services/api';
import type { BatchPlan, BatchTemplateSummary, MfgTemplatePackageSummary } from '../../types';

interface CreateBatchModalV4Props {
    visible: boolean;
    onCancel: () => void;
    onSuccess: () => void;
    initialValues?: BatchPlan | null;
    templates?: BatchTemplateSummary[];
    mfgPackages?: MfgTemplatePackageSummary[];
}

interface CreateBatchFormValues {
    source_kind: 'template' | 'package';
    template_id?: number;
    mfg_package_id?: number;
    // 恒为基准日期 Day0。与库里 planned_start_date（最早工序日）的换算全部在后端完成
    day0_date: Dayjs;
    batch_code: string;
    batch_name: string;
    plan_status: BatchPlan['plan_status'];
}

type CreateBatchErrors = Partial<Record<keyof CreateBatchFormValues, string>>;

const getApiErrorMessage = (error: unknown, fallback: string) => {
    const responseData = (error as { response?: { data?: { error?: unknown; message?: unknown } } })?.response?.data;
    const message = responseData?.error ?? responseData?.message;
    return typeof message === 'string' && message.trim() ? message : fallback;
};

const createDefaultValues = (): CreateBatchFormValues => ({
    source_kind: 'template',
    template_id: undefined,
    mfg_package_id: undefined,
    day0_date: dayjs(),
    batch_code: '',
    batch_name: '',
    plan_status: 'DRAFT',
});

const CreateBatchModalV4: React.FC<CreateBatchModalV4Props> = ({
    visible,
    onCancel,
    onSuccess,
    initialValues,
    templates: templateOptions,
    mfgPackages = [],
}) => {
    const [loading, setLoading] = useState(false);
    const [templates, setTemplates] = useState<BatchTemplateSummary[]>(templateOptions ?? []);
    const [values, setValues] = useState<CreateBatchFormValues>(createDefaultValues);
    const [errors, setErrors] = useState<CreateBatchErrors>({});
    const [day0Offset, setDay0Offset] = useState<{ offset: number; has_pre_day0: boolean; pre_day0_count: number } | null>(null);
    // 换模板/改开工日命中已排班批次时，后端返回 requiresConfirmation；此处暂存待确认重建的上下文
    const [rebuildConfirm, setRebuildConfirm] = useState<{
        id: number;
        payload: BatchPlanPayload;
        blocking: Array<{ label: string; count: number }>;
    } | null>(null);
    const [rebuildLoading, setRebuildLoading] = useState(false);

    // day0Offset 仅驱动「提前投料」提示横幅，不参与提交换算；
    // 序号守卫防止快速切换模板时旧响应晚到覆盖新模板的提示
    const offsetRequestSeq = useRef(0);
    const handleTemplateChange = useCallback(async (templateId?: number) => {
        const seq = ++offsetRequestSeq.current;
        if (!templateId) {
            setDay0Offset(null);
            return;
        }

        try {
            const data = await batchPlanApi.getTemplateDay0Offset(templateId);
            if (seq === offsetRequestSeq.current) {
                setDay0Offset(data);
            }
        } catch (error) {
            console.warn('Failed to load template offset', error);
            if (seq === offsetRequestSeq.current) {
                setDay0Offset(null);
            }
        }
    }, []);

    useEffect(() => {
        if (templateOptions && templateOptions.length > 0) {
            setTemplates(templateOptions);
            return;
        }

        if (!visible) {
            return;
        }

        batchPlanApi.getTemplates().then(setTemplates).catch((error) => {
            console.error('Failed to load templates', error);
            wxbToast.error('加载工艺模板失败');
        });
    }, [templateOptions, visible]);

    useEffect(() => {
        if (!visible) {
            return;
        }

        setErrors({});
        setDay0Offset(null);

        if (initialValues) {
            // day0_date 由后端按模板 min_day 从最早工序日还原；旧缓存行没有该字段时
            // 退回 planned_start_date（无提前投料的模板两者相等）
            const initialDay0 = initialValues.day0_date ?? initialValues.planned_start_date;
            const nextValues: CreateBatchFormValues = {
                source_kind: initialValues.mfg_package_id ? 'package' : 'template',
                template_id: initialValues.template_id,
                mfg_package_id: initialValues.mfg_package_id ?? undefined,
                day0_date: initialDay0 ? dayjs(initialDay0) : dayjs(),
                batch_code: initialValues.batch_code,
                batch_name: initialValues.batch_name,
                plan_status: initialValues.plan_status,
            };
            setValues(nextValues);
            handleTemplateChange(initialValues.template_id);
            return;
        }

        setValues(createDefaultValues());
    }, [handleTemplateChange, initialValues, visible]);

    const actualStartPreview = useMemo(() => {
        if (!day0Offset || day0Offset.offset >= 0 || !values.day0_date?.isValid()) {
            return null;
        }
        return values.day0_date.add(day0Offset.offset, 'day').format('YYYY-MM-DD');
    }, [day0Offset, values.day0_date]);

    const selectedPackage = useMemo(
        () => mfgPackages.find((item) => item.id === values.mfg_package_id) ?? null,
        [mfgPackages, values.mfg_package_id],
    );

    const updateValue = useCallback(<K extends keyof CreateBatchFormValues>(
        key: K,
        value: CreateBatchFormValues[K],
    ) => {
        setValues((current) => ({ ...current, [key]: value }));
        setErrors((current) => ({ ...current, [key]: undefined }));
    }, []);

    const validate = useCallback(() => {
        const nextErrors: CreateBatchErrors = {};

        if (values.source_kind === 'template' && !values.template_id) {
            nextErrors.template_id = '请选择模板';
        }
        if (values.source_kind === 'package' && !values.mfg_package_id) {
            nextErrors.mfg_package_id = '请选择总包';
        }
        if (!values.day0_date?.isValid()) {
            nextErrors.day0_date = '请选择日期';
        }
        if (!values.batch_code.trim()) {
            nextErrors.batch_code = '请输入批次代码';
        }
        if (!values.batch_name.trim()) {
            nextErrors.batch_name = '请输入批次名称';
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    }, [values]);

    const handleSubmit = useCallback(async () => {
        if (!validate()) {
            return;
        }

        setLoading(true);
        try {
            if (!initialValues && values.source_kind === 'package') {
                const result = await batchPlanApi.createFromPackage({
                    mfg_package_id: Number(values.mfg_package_id),
                    batch_code: values.batch_code.trim(),
                    batch_name: values.batch_name.trim(),
                    // 总包接口的 planned_start_date 即总包基准日，偏移由后端 service 换算
                    planned_start_date: values.day0_date.format('YYYY-MM-DD'),
                });
                wxbToast.success(result.message || '已按总包创建部门批次');
                onSuccess();
                return;
            }

            const templateId = values.template_id ?? initialValues?.template_id;
            if (!templateId) {
                wxbToast.error('请选择模板');
                return;
            }

            const payload = {
                template_id: templateId,
                batch_code: values.batch_code.trim(),
                batch_name: values.batch_name.trim(),
                plan_status: values.plan_status,
                day0_date: values.day0_date.format('YYYY-MM-DD'),
            };

            if (initialValues) {
                try {
                    await batchPlanApi.update(initialValues.id, payload);
                } catch (error: any) {
                    const data = error?.response?.data;
                    if (error?.response?.status === 409 && data?.requiresConfirmation) {
                        // 批次已排班：弹二次确认框，确认后清空排班并强制重建（此处不当作错误提示）
                        setRebuildConfirm({
                            id: initialValues.id,
                            payload,
                            blocking: Array.isArray(data?.details?.blocking) ? data.details.blocking : [],
                        });
                        return;
                    }
                    throw error;
                }
                wxbToast.success('批次已更新');
            } else {
                await batchPlanApi.create(payload);
                wxbToast.success('批次已创建');
            }
            onSuccess();
        } catch (error) {
            console.error('Failed to save batch', error);
            wxbToast.error(getApiErrorMessage(error, initialValues ? '更新批次失败' : '创建批次失败'));
        } finally {
            setLoading(false);
        }
    }, [initialValues, onSuccess, validate, values]);

    // 二次确认后强制重建：清空原排班/加班/冲突数据并按新模板重新生成操作计划
    const handleConfirmRebuild = useCallback(async () => {
        if (!rebuildConfirm) {
            return;
        }
        setRebuildLoading(true);
        try {
            await batchPlanApi.update(rebuildConfirm.id, { ...rebuildConfirm.payload, force_rebuild: true });
            wxbToast.success('批次已更新，原排班数据已清空并按新模板重建');
            setRebuildConfirm(null);
            onSuccess();
        } catch (error) {
            console.error('Failed to rebuild batch', error);
            wxbToast.error(getApiErrorMessage(error, '重建批次失败'));
        } finally {
            setRebuildLoading(false);
        }
    }, [rebuildConfirm, onSuccess]);

    return (
        <>
        <WxbModal
            open={visible}
            className="batch-modal-v4__modal"
            title={initialValues ? '编辑批次' : '新建生产批次'}
            okText={initialValues ? '保存更改' : '立即创建'}
            cancelText="取消"
            confirmLoading={loading}
            onOk={handleSubmit}
            onCancel={onCancel}
            width={560}
            centered
        >
            <div className="batch-modal-v4__body">
                <p className="batch-modal-v4__intro">
                    选择标准模板会生成单个批次；选择 MFG 总包会按包含的模板生成各部门独立批次。
                </p>

                <div className="batch-modal-v4__section">
                    <h3 className="batch-modal-v4__section-title">计划来源与日期</h3>
                    {!initialValues && (
                        <WxbSegmented
                            size="md"
                            value={values.source_kind}
                            onChange={(value) => {
                                const sourceKind = value as CreateBatchFormValues['source_kind'];
                                updateValue('source_kind', sourceKind);
                                setDay0Offset(null);
                            }}
                            options={[
                                { label: '工艺模板', value: 'template', icon: <WxbIcon name="recipe" size={14} /> },
                                { label: 'MFG 总包', value: 'package', icon: <WxbIcon name="batch-record" size={14} /> },
                            ]}
                        />
                    )}

                    {values.source_kind === 'template' ? (
                        <WxbSelect
                            label="工艺模板"
                            placeholder="选择一个标准生产流程"
                            value={values.template_id}
                            error={errors.template_id}
                            options={templates.map((template) => ({
                                label: template.template_name,
                                value: template.id,
                            }))}
                            onChange={(value) => {
                                const templateId = value as number;
                                updateValue('template_id', templateId);
                                handleTemplateChange(templateId);
                            }}
                        />
                    ) : (
                        <WxbSelect
                            label="MFG 总包"
                            placeholder="选择一个生产联动总包"
                            value={values.mfg_package_id}
                            error={errors.mfg_package_id}
                            disabled={Boolean(initialValues)}
                            options={mfgPackages.map((item) => ({
                                label: `${item.package_code} · ${item.package_name}`,
                                value: item.id,
                            }))}
                            onChange={(value) => updateValue('mfg_package_id', value as number)}
                        />
                    )}
                    <WxbDatePicker
                        label={values.source_kind === 'package' ? '总包基准日期 (Day 0)' : '基准日期 (Day 0)'}
                        format="YYYY-MM-DD"
                        value={values.day0_date}
                        error={errors.day0_date}
                        disabled={Boolean(initialValues?.mfg_package_id)}
                        onChange={(date) => updateValue('day0_date', (date ?? dayjs()) as Dayjs)}
                    />
                    {selectedPackage && (
                        <div className="batch-modal-v4__info" role="status">
                            <WxbIcon name="kanban" size={18} />
                            <div>
                                <div className="batch-modal-v4__warning-title">总包生成范围</div>
                                <p className="batch-modal-v4__warning-copy">
                                    {selectedPackage.package_name}
                                    {`，将生成 ${selectedPackage.module_count} 个部门批次`}
                                    {selectedPackage.total_days ? `，预计 ${selectedPackage.total_days} 天` : ''}
                                    {selectedPackage.min_day !== null && selectedPackage.min_day !== undefined
                                        ? `，覆盖 Day ${selectedPackage.min_day} 到 Day ${selectedPackage.max_day}`
                                        : ''}
                                </p>
                            </div>
                        </div>
                    )}
                    {day0Offset?.has_pre_day0 && (
                        <div className="batch-modal-v4__warning" role="status">
                            <WxbIcon name="oos-clock" size={18} />
                            <div>
                                <div className="batch-modal-v4__warning-title">提前投料提醒</div>
                                <p className="batch-modal-v4__warning-copy">
                                    该模板包含提前 {day0Offset.pre_day0_count} 天的操作。
                                    实际开始日期将自动调整为 {actualStartPreview ?? '-'}。
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="batch-modal-v4__section">
                    <h3 className="batch-modal-v4__section-title">批次身份</h3>
                    <div className="batch-modal-v4__grid">
                        <WxbInput
                            label="批次代码"
                            placeholder="BATCH-001"
                            value={values.batch_code}
                            error={errors.batch_code}
                            onChange={(event) => updateValue('batch_code', event.target.value)}
                        />
                        <WxbSelect
                            label="状态"
                            value={values.plan_status}
                            options={[
                                { label: '草稿', value: 'DRAFT' },
                                { label: '已激活', value: 'ACTIVATED' },
                            ]}
                            onChange={(value) => updateValue('plan_status', value as BatchPlan['plan_status'])}
                        />
                    </div>
                    <WxbInput
                        label="批次名称"
                        placeholder="输入易于识别的名称"
                        value={values.batch_name}
                        error={errors.batch_name}
                        onChange={(event) => updateValue('batch_name', event.target.value)}
                    />
                </div>
            </div>
        </WxbModal>

        <WxbModal
            open={!!rebuildConfirm}
            className="batch-modal-v4__rebuild-modal"
            title="更换模板将清空现有排班"
            okText="确认重建"
            cancelText="取消"
            okVariant="danger"
            confirmLoading={rebuildLoading}
            onOk={handleConfirmRebuild}
            onCancel={() => setRebuildConfirm(null)}
            width={460}
            centered
        >
            <div className="batch-modal-v4__rebuild-confirm">
                <p>
                    该批次已存在下游排班数据
                    {rebuildConfirm && rebuildConfirm.blocking.length > 0
                        ? `（${rebuildConfirm.blocking.map((b) => `${b.label} ${b.count} 条`).join('、')}）`
                        : ''}
                    。
                </p>
                <p>
                    继续将<strong>清空这些排班 / 加班 / 冲突数据</strong>，并按新模板重新生成操作计划。该操作不可撤销，确认继续？
                </p>
            </div>
        </WxbModal>
        </>
    );
};

export default CreateBatchModalV4;
